import { Button } from "PFComponents/button";
import Tooltip from "PFComponents/tooltip/tooltip";
import { IconName } from "PFTheme/graphics/icons";
import { CustomValue, CustomValueMetaActionName } from "PFTypes";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSkillActionPermissions } from "../../hooks/use_skill_action_permissions";
import { useSkillActions } from "../../table/hooks/use_skill_actions";
import { useSkillManagementModal } from "../../table/hooks/use_skill_management_modal";
import { useSkillsCustomTypeId } from "../../utils/use_skills_custom_type";
import { SkillsManagementModalType } from "../modals/import/constants/skills_management_modal_type";
import { Section } from "./components/section";
import css from "./skills_management_side_panel.module.scss";

type SidePanelActionsProps = { skill: CustomValue };

type Action = {
  id: CustomValueMetaActionName;
  tooltip: string;
  action: () => void;
  disabled?: boolean;
  iconName?: IconName;
};

export const SidePanelActions = ({ skill }: SidePanelActionsProps) => {
  const { t } = useTranslation("skillsManagement", { keyPrefix: "bulkActions.titles" });
  const { openModal } = useSkillManagementModal();
  const { approveSkills, blacklistSkills, exportSkills, resetSkills } = useSkillActions();
  const customTypeId = useSkillsCustomTypeId();
  const { isActionDisabledMap, isGlobalSkill } = useSkillActionPermissions(skill);

  const actions: Action[] = useMemo(
    () => [
      {
        id: "edit",
        tooltip: isActionDisabledMap.edit ? t("cannotEdit") : t("edit"),
        action: () => openModal(SkillsManagementModalType.Edit, { skills: [skill] }),
        disabled: isActionDisabledMap.edit,
        iconName: "edit"
      },
      {
        id: "approve",
        tooltip: isActionDisabledMap.approve ? t("cannotApprove", { count: 1 }) : t("approve"),
        action: () => approveSkills({ ids: [skill.id], customTypeId }),
        disabled: isActionDisabledMap.approve,
        iconName: "check"
      },
      {
        id: "block",
        tooltip: isActionDisabledMap.block ? t("cannotBlock", { count: 1 }) : t("block"),
        action: () => blacklistSkills({ ids: [skill.id], customTypeId }),
        disabled: isActionDisabledMap.block,
        iconName: "forbidden"
      },
      {
        id: "merge",
        tooltip: isActionDisabledMap.merge
          ? t(isGlobalSkill ? "cannotMergeGlobalSkills" : "cannotMerge", { count: 1 })
          : t("merge"),
        action: () => openModal(SkillsManagementModalType.Merge, { skills: [skill] }),
        disabled: isActionDisabledMap.merge,
        iconName: "merge"
      },
      {
        id: "export",
        tooltip: t("export"),
        action: () => exportSkills({ ids: [skill.id], customTypeId }),
        iconName: "export"
      },
      {
        id: "reset",
        iconName: "history",
        tooltip: t("reset"),
        action: () => resetSkills({ ids: [skill.id], customTypeId }),
        disabled: isActionDisabledMap.reset
      }
    ],
    [
      isActionDisabledMap,
      t,
      isGlobalSkill,
      openModal,
      skill,
      approveSkills,
      customTypeId,
      blacklistSkills,
      exportSkills,
      resetSkills
    ]
  );

  return (
    <Section>
      <div className={css.skillActions}>
        {actions.map(({ id, action, iconName, tooltip, disabled }) => (
          <Tooltip content={tooltip} placement="top" key={id}>
            <span>
              <Button kind="ghost" icon={iconName} onClick={action} disabled={disabled} />
            </span>
          </Tooltip>
        ))}
      </div>
    </Section>
  );
};
